import React, {
    useCallback, useContext, useState, useRef,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import isSameWeek from 'date-fns/is_same_week';
import { useReactiveVar } from '@apollo/client';
import { Portal } from 'react-portal';
import queryString from 'query-string';

import { analyticService } from 'global/services';

import { selectedPeriodVar } from 'app/apollo/reaction';

import { LocaleContext } from 'app/containers/LocaleProvider';
import { UIHeading } from 'app/components/ui';
import { DialogAnimated } from 'app/components/Dialog';
import { upperPricesDishIds as newTrialDishes } from 'app/const/links/trialFirstOrderSet';

import { CheckoutStoreContext } from '../../store/checkoutStore.context';


import { StepLayout } from '../StepLayout/StepLayout';
import { RENDER_CONTEXT_SINGLE_STEP, RENDER_CONTEXT_STEP_BY_STEP } from '../stepByStepCheckoutConst';

import { DatetimeSelect } from './DatetimeSelect';

import '../StepLayout/stepLayout.scss';


const datetimeLocales = {
    ru: {
        headerTitle: 'Оформление заказа',
        title: 'Дата и время',
    },
    en: {
        headerTitle: 'Checkout',
        title: 'Date and time',
    },
};

const trialDishes = ['1921', '2599', '3009', '3011', '4135'];


export function StepDatetime({
    renderContext = RENDER_CONTEXT_STEP_BY_STEP,
    headerTitle: headerTitleProp = null,
    deliveryDates,
    timeIntervals,
    selectDay = null,
    selectTime: { timeIndex },
    disabled = false,
    deliveryPrice,
    onSelectDate,
    onSelectDateInNextPeriod,
}) {
    // eslint-disable-next-line prefer-rest-params
    const props = arguments[0];

    const { locale } = useContext(LocaleContext);
    const { dispatch } = useContext(CheckoutStoreContext);

    const selectedPeriod = useReactiveVar(selectedPeriodVar);


    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const selectingDate = useRef('');

    const [isConfirmLoading, setIsConfirmLoading] = useState(false);


    useEffect(
        () => {
            if (isDialogOpen) {
                analyticService.push({ eventName: 'Checkout_Popup_Menu_Will_Change' });
            }
        },
        [isDialogOpen],
    );

    const queryFromUrl = queryString.parse(window.location.search);
    const dishesFromSchema = queryFromUrl?.dishes?.split('.');
    const hasTrialDishes = (dishesFromSchema?.every((dish) => trialDishes.includes(dish))
        || dishesFromSchema?.every((dish) => newTrialDishes.includes(dish)))
        && dishesFromSchema.length === trialDishes.length;


    const handleSelectDate = useCallback((nextDate) => {
        analyticService.push({
            eventName: 'Checkout_Click_Date',
        });

        const isDateInCurrentPeriod = isSameWeek(selectedPeriod, nextDate, { weekStartsOn: 1 });

        if (isDateInCurrentPeriod || hasTrialDishes) {
            onSelectDate(nextDate);
        } else {
            selectingDate.current = nextDate;
            setIsDialogOpen(true);
        }
    }, [onSelectDate, selectedPeriod, hasTrialDishes]);

    const handleCloseDialog = useCallback((e) => {
        if (isConfirmLoading) return;

        setIsDialogOpen(false);
        selectingDate.current = '';


        // note: Метод может быть вызван программно и тогда е будет event
        if (e) {
            const { currentTarget } = e;
            const eventData = currentTarget.id === 'close-cross'
                ? {
                    eventName: 'Checkout_Close_Popup_Menu_Will_Change',
                } : {
                    eventName: 'Checkout_Click_Popup_Menu_Will_Change',
                    eventAction: 'not accept',
                };
            analyticService.push(eventData);
        }
    }, [isConfirmLoading]);

    const handleConfirmDate = useCallback(async () => {
        if (isConfirmLoading) return;

        setIsConfirmLoading(true);

        const nextDate = selectingDate.current;
        await onSelectDate(nextDate);
        await onSelectDateInNextPeriod(nextDate);
        handleCloseDialog();

        setIsConfirmLoading(false);
        analyticService.push({
            eventName: 'Checkout_Click_Popup_Menu_Will_Change',
            eventAction: 'accept',
        });
    }, [onSelectDate, onSelectDateInNextPeriod, handleCloseDialog, isConfirmLoading]);


    const onSelectInterval = useCallback((nextIndex) => {
        analyticService.push({
            eventName: 'Checkout_Click_Time',
        });
        dispatch({
            type: 'SELECT_TIME_INTERVAL_ITEM',
            payload: { timeIndex: nextIndex },
        });
    }, [dispatch]);

    useEffect(
        () => {
            dispatch({
                type: 'SET_IS_DATE_TIME_POPUP_OPENNED',
                payload: { isDateTimePopupOpenned: true },
            });

            return () => {
                dispatch({
                    type: 'SET_IS_DATE_TIME_POPUP_OPENNED',
                    payload: { isDateTimePopupOpenned: false },
                });
            };
        },
        [dispatch],
    );


    const headerTitle = headerTitleProp || datetimeLocales[locale].headerTitle;

    const hasSelectsData = selectDay && (timeIndex > 0 || timeIndex === 0);

    return (
        <>
            <StepLayout
                {...props}
                isNextStepButtonDisabled={disabled}
                title={headerTitle}
            >
                {renderContext === RENDER_CONTEXT_SINGLE_STEP
                    ? null
                    : (
                        <UIHeading level="3.2" styleName="step-layout__content-header">
                            {datetimeLocales[locale].title}
                        </UIHeading>
                    )}

                {hasSelectsData ? (
                    <DatetimeSelect
                        deliveryDates={deliveryDates}
                        timeIntervals={timeIntervals}
                        selectedDate={selectDay}
                        onSelectDate={handleSelectDate}
                        selectedIntervalIndex={timeIndex}
                        disabled={disabled}
                        deliveryPrice={deliveryPrice}
                        onSelectInterval={onSelectInterval}
                    />
                ) : null}
            </StepLayout>
            <Portal>
                <DialogAnimated
                    isOpen={isDialogOpen}
                    strongText="Будет новое меню"
                    regularText={(
                        <>
                            Мы подберем аналогичные блюда
                            <br />
                            Переносим дату доставки?
                        </>
                    )}
                    confirmText="Перенести"
                    rejectText="Не надо"
                    oneRowButtons
                    onReject={handleCloseDialog}
                    onConfirm={handleConfirmDate}
                    isConfirmLoading={isConfirmLoading}
                    extraButtonPosition="first"
                />
            </Portal>
        </>
    );
}

StepDatetime.propTypes = {
    renderContext: PropTypes.oneOf([RENDER_CONTEXT_SINGLE_STEP, RENDER_CONTEXT_STEP_BY_STEP]),
    headerTitle: PropTypes.string,
    deliveryDates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    timeIntervals: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    deliveryPrice: PropTypes.number.isRequired,
    selectDay: PropTypes.string,
    selectTime: PropTypes.shape({
        timeIndex: PropTypes.number,
    }).isRequired,
    disabled: PropTypes.bool,
    onSelectDate: PropTypes.func.isRequired,
    onSelectDateInNextPeriod: PropTypes.func.isRequired,
};
